import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import { StyledElement } from "../../../../Styles/GlobalStyles";
import { ReactComponent as DownloadIcon } from "../../../../assets/icons/DownloadIcon.svg";
import { ReactComponent as UploadIcon } from "../../../../assets/icons/UploadIcon.svg";
import { Spinner } from "../../../../components/shared/LoadingSpinner/Spinner";

const SpeedTestHistory = ({
  speedTestHistory,
  historyExpanded,
  setHistoryExpanded,
  speedTestHistoryLoading,
}: any) => {
  const formatHistoryDateTime = (value: any): string => {
    if (value === null || value === undefined) return "--";
    const raw = String(value).trim();
    if (!raw) return "--";

    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return "--";

    const formattedDate = parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const formattedTime = parsed.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return `${formattedDate} - ${formattedTime}`;
  };

  const formatMetricWithThreeDecimals = (value: any): string => {
    if (value === null || value === undefined) return "--";
    const raw = String(value).trim();
    if (!raw) return "--";

    const match = raw.match(/^(-?\d+(?:\.\d+)?)(?:\s*([a-zA-Z%/]+))?$/);
    if (!match) return "--";

    const numericValue = Number.parseFloat(match[1]);
    if (!Number.isFinite(numericValue)) return "--";

    const unit = match[2]?.trim() || "";
    const formattedValue = numericValue.toFixed(3);
    return unit ? `${formattedValue} ${unit}` : formattedValue;
  };


  return (
    <Box>
      <Accordion
        disableGutters
        elevation={0}
        expanded={historyExpanded}
        onChange={(_, expanded) => setHistoryExpanded(expanded)}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
          "&::before": {
            display: "none",
          },
        }}>
        <AccordionSummary
          aria-controls="speed-test-history-content"
          id="speed-test-history-header"
          sx={{
            minHeight: "48px !important",
            px: { xs: 2, md: 3 },
            backgroundColor: "#E9F3EB",
            color: "#067D17",
            justifyContent: "center",
            "& .MuiAccordionSummary-content": {
              my: "14px !important",
              flexGrow: 0,
              justifyContent: "center",
            },
            "& .MuiAccordionSummary-expandIconWrapper": {
              transform: "none !important",
              marginLeft: "8px",
            },
          }}>
          <Typography fontSize={16} fontWeight={500}>
            View past speed test results
          </Typography>
          <StyledElement textcolor="green">
            {historyExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </StyledElement>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {speedTestHistoryLoading ? (
            <Box my={5}>
              <Spinner />
            </Box>
          ) : speedTestHistory?.length > 0 ? (
            <Box
              px={{ xs: 2, md: 3 }}
              height={"360px"}
              overflow={"auto"}
              sx={{ scrollbarWidth: "thin" }}>
              <Grid container columnSpacing={2} py={2.5}>
                <Grid item xs={5} flex={1}>
                  <Typography fontSize={14} fontWeight={700}>
                    Date & Time
                  </Typography>
                </Grid>
                <Grid item xs={2.5}>
                  <Typography fontSize={14} fontWeight={700} textAlign="left">
                    Download
                  </Typography>
                </Grid>
                <Grid item xs={2.5}>
                  <Typography fontSize={14} fontWeight={700} textAlign="left">
                    Upload
                  </Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography fontSize={14} fontWeight={700} textAlign="left">
                    Latency
                  </Typography>
                </Grid>
              </Grid>

              {[...speedTestHistory]
                .sort(
                  (a: any, b: any) =>
                    new Date(b?.timestamp || "").getTime() - new Date(a?.timestamp || "").getTime()
                )
                .map((historyItem: any, index: number) => (
                  <Grid
                    container
                    key={`${historyItem?.timestamp || "history"}-${index}`}
                    columnSpacing={2}
                    py={2.5}
                    borderBottom={
                      index === speedTestHistory.length - 1 ? "none" : "1px solid #f3f4f6"
                    }>
                    <Grid item xs={5} flex={1}>
                      <Typography fontSize={14} fontWeight={500} lineHeight={1.25}>
                        {formatHistoryDateTime(historyItem?.timestamp)}
                      </Typography>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Grid container alignItems="center" columnGap={0.75} wrap="nowrap">
                        <Box
                          component={DownloadIcon}
                          aria-label="Download"
                          sx={{
                            width: 10,
                            height: 10,
                            "& path": {
                              stroke: "#009424",
                            },
                          }}
                        />
                        <Typography
                          fontSize={14}
                          fontWeight={500}
                          color="#009424"
                          lineHeight={1.25}>
                          {formatMetricWithThreeDecimals(historyItem?.downloadSpeed)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={2.5}>
                      <Grid container alignItems="center" columnGap={0.75} wrap="nowrap">
                        <Box
                          component={UploadIcon}
                          aria-label="Upload"
                          sx={{
                            width: 10,
                            height: 10,
                            "& path": {
                              stroke: "#009424",
                            },
                          }}
                        />
                        <Typography
                          fontSize={14}
                          fontWeight={500}
                          color="#009424"
                          lineHeight={1.25}>
                          {formatMetricWithThreeDecimals(historyItem?.uploadSpeed)}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography fontSize={14} fontWeight={500} lineHeight={1.25}>
                        {formatMetricWithThreeDecimals(historyItem?.ping)}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
            </Box>
          ) : (
            <Box py={4} display="flex" justifyContent="center">
              <Typography fontSize={14} fontWeight={500} color="#6B7280">
                No speed test history available.
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SpeedTestHistory;
